import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Center } from '@chakra-ui/react';
import { Page, Quote } from '@shinetools/invoicing-renderer';
import {
  calculateInvoiceTaskTotals,
  calculateInvoiceTotals,
} from '@shinetools/invoicing-rules';
import { Feature } from '@shinetools/pricing-plan-library';

import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import LanguageSwitch from 'features/Work/components/LanguageSwitch';
import { LanguageContext } from 'features/Work/components/LanguageSwitch/hooks';
import { QUOTE_VALIDITY_DAYS_UNLIMITED } from 'features/Work/constants';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';

import { QuoteStatus } from '../../../../../../../__generated__/GQL';
import { computeQuoteSpecialMentions } from '../../../../../utils/computeSpecialMentions';
import { InvoicingSpecialMentions } from '../../../../hooks/useInvoicingSpecialMentionsQuery';
import { useQuoteFormContext } from '../context';

interface QuotePreviewProps {
  invoicingSpecialMentions: InvoicingSpecialMentions;
}
const QuotePreview: FC<QuotePreviewProps> = ({ invoicingSpecialMentions }) => {
  const defaultLang = useCurrentLanguage();
  const { isFeatureAvailable } = useCurrentPricingPlan();
  const [lang, setLang] = useState(defaultLang);

  const { clients, company, companySettings } = useQuoteFormContext();
  const { watch } = useFormContext<QuoteFormSchema>();

  const quote = watch();

  const client = clients.find((c) => c.invoiceClientId === quote.clientId);

  const formattedTasks = quote.tasks.map((task) => ({
    ...task,
    ...calculateInvoiceTaskTotals(task),
  }));

  const { totalAmountAfterTaxes, totalAmountBeforeTaxes, totalVatAmount } =
    calculateInvoiceTotals(formattedTasks);

  const { particularMentions, paymentMentions } = computeQuoteSpecialMentions(
    {
      client,
      company: company,
      lateFeePercentage: quote.lateFeePercentage,
      lateMaxDays: quote.lateMaxDays,
      startAt: quote.startAt,
      tasks: formattedTasks,
    },
    invoicingSpecialMentions,
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      <Center>
        <Box>
          <LanguageSwitch />

          <Page>
            <Quote
              lang={lang}
              quote={{
                ...quote,
                client,
                company,
                logoUrl: isFeatureAvailable(Feature.PersonalizedLogo)
                  ? companySettings?.logoUrl ?? ''
                  : '',
                particularMentions,
                paymentMentions,
                settings: {
                  ...quote.settings,
                  quoteValidityDays:
                    quote.settings.quoteValidityDays ??
                    QUOTE_VALIDITY_DAYS_UNLIMITED,
                },
                // At this point, the invoice has not been sent
                status: QuoteStatus.Pending,
                tasks: formattedTasks,
                totalAmountAfterTaxes,
                totalAmountBeforeTaxes,
                totalVatAmount,
              }}
            />
          </Page>
        </Box>
      </Center>
    </LanguageContext.Provider>
  );
};

export default QuotePreview;
